import type { DriveAuthSession } from '../types';

export interface DropboxConnectionStatus {
  connected: boolean;
  account?: {
    accountId: string;
    email?: string;
    displayName?: string;
  };
  linkedAt?: string;
  accountId?: string;
  email?: string;
  displayName?: string;
  hasCursor?: boolean;
  cursorPath?: string;
}

export interface DropboxImportedItem {
  fileId: string;
  name: string;
  mimeType: string;
  source: 'dropbox';
  externalLink?: string;
  thumbnailLink?: string;
  modifiedTime?: string;
  size?: string;
}

export interface DropboxImportResponse {
  items: DropboxImportedItem[];
  cursor: string;
  folderPath: string;
}

export interface DropboxSyncResponse {
  newItems: DropboxImportedItem[];
  updatedItems: DropboxImportedItem[];
  deletedIds: string[];
  cursor: string;
}

const DROPBOX_APP_KEY = import.meta.env.VITE_DROPBOX_APP_KEY || '';
const DROPBOX_OAUTH_POPUP_NAME = 'dropbox-oauth';
const DROPBOX_OAUTH_CALLBACK_SOURCE = 'dropbox-oauth-callback';
const DROPBOX_OAUTH_TIMEOUT_MS = 2 * 60 * 1000;

const authHeaders = (session: DriveAuthSession) => ({
  Authorization: `Bearer ${session.idToken || session.accessToken}`,
  'x-access-token': session.accessToken,
  'Content-Type': 'application/json',
});

const randomString = (length: number): string => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes).map((b) => alphabet[b % alphabet.length]).join('');
};

const base64UrlEncode = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const pkceChallenge = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(digest);
};

const openOAuthPopup = (url: string): Window | null => {
  const width = 520;
  const height = 760;
  const left = window.screenX + Math.max(0, Math.floor((window.outerWidth - width) / 2));
  const top = window.screenY + Math.max(0, Math.floor((window.outerHeight - height) / 2));
  return window.open(
    url,
    DROPBOX_OAUTH_POPUP_NAME,
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
  );
};

export const isDropboxConfigured = (): boolean => Boolean(DROPBOX_APP_KEY);

export const getDropboxSession = async (session: DriveAuthSession): Promise<DropboxConnectionStatus> => {
  const response = await fetch('/api/dropbox/session', {
    method: 'GET',
    cache: 'no-store',
    headers: authHeaders(session),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch Dropbox session status.');
  }
  return response.json();
};

export const disconnectDropbox = async (session: DriveAuthSession): Promise<void> => {
  const response = await fetch('/api/dropbox/session', {
    method: 'DELETE',
    headers: authHeaders(session),
  });
  if (!response.ok) {
    throw new Error('Failed to disconnect Dropbox.');
  }
};

export const connectDropbox = async (session: DriveAuthSession): Promise<DropboxConnectionStatus> => {
  if (!DROPBOX_APP_KEY) {
    throw new Error('Dropbox is not configured. Set VITE_DROPBOX_APP_KEY.');
  }

  const codeVerifier = randomString(96);
  const state = randomString(24);
  const codeChallenge = await pkceChallenge(codeVerifier);
  const redirectUri = `${window.location.origin}/dropbox-auth-callback.html`;
  const authorizeUrl = new URL('https://www.dropbox.com/oauth2/authorize');
  authorizeUrl.searchParams.set('client_id', DROPBOX_APP_KEY);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('token_access_type', 'offline');
  authorizeUrl.searchParams.set('redirect_uri', redirectUri);
  authorizeUrl.searchParams.set('code_challenge', codeChallenge);
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');
  authorizeUrl.searchParams.set('state', state);

  const popup = openOAuthPopup(authorizeUrl.toString());
  if (!popup) {
    throw new Error('Popup blocked. Please allow popups and try Dropbox connect again.');
  }

  const code = await new Promise<string>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      try {
        popup.close();
      } catch {}
      reject(new Error('Dropbox OAuth timed out. Please try again.'));
    }, DROPBOX_OAUTH_TIMEOUT_MS);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data || {};
      if (payload?.source !== DROPBOX_OAUTH_CALLBACK_SOURCE) return;

      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      try {
        popup.close();
      } catch {}

      if (payload.error) {
        reject(new Error(String(payload.error)));
        return;
      }
      if (payload.state !== state) {
        reject(new Error('Dropbox OAuth state mismatch. Please retry.'));
        return;
      }
      if (!payload.code) {
        reject(new Error('Dropbox OAuth did not return an authorization code.'));
        return;
      }
      resolve(String(payload.code));
    };

    window.addEventListener('message', onMessage);
  });

  const response = await fetch('/api/dropbox/connect', {
    method: 'POST',
    headers: authHeaders(session),
    body: JSON.stringify({
      code,
      codeVerifier,
      redirectUri,
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || 'Failed to complete Dropbox OAuth connection.');
  }
  const connectedPayload = (await response.json().catch(() => null)) as DropboxConnectionStatus | null;
  // Always re-fetch with no-store to synchronize client state after connect.
  const freshSession = await getDropboxSession(session).catch(() => connectedPayload || { connected: false });
  return freshSession?.connected ? freshSession : (connectedPayload || { connected: false });
};

export const importDropboxFolder = async (
  session: DriveAuthSession,
  folderPath: string,
  recursive = true,
): Promise<DropboxImportResponse> => {
  const response = await fetch('/api/dropbox/import', {
    method: 'POST',
    headers: authHeaders(session),
    body: JSON.stringify({ folderPath, recursive }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || 'Dropbox folder import failed.');
  }
  return response.json();
};

export const syncDropbox = async (
  session: DriveAuthSession,
  cursor?: string,
): Promise<DropboxSyncResponse> => {
  const response = await fetch('/api/dropbox/sync', {
    method: 'POST',
    headers: authHeaders(session),
    body: JSON.stringify({ cursor }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || 'Dropbox incremental sync failed.');
  }
  return response.json();
};

export const getDropboxTemporaryLink = async (
  session: DriveAuthSession,
  fileId: string,
): Promise<string> => {
  const response = await fetch(`/api/dropbox/temporary-link?id=${encodeURIComponent(fileId)}`, {
    method: 'GET',
    headers: authHeaders(session),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || 'Failed to get Dropbox file link.');
  }
  const payload = await response.json();
  return String(payload?.link || '');
};
